import axios, { AxiosError, type InternalAxiosRequestConfig } from "axios";
import { env } from "./env";
import { useAuthStore } from "@/stores/auth.store";

export const api = axios.create({
  baseURL: env.VITE_API_BASE_URL,
  timeout: env.VITE_API_TIMEOUT,
  headers: { "Content-Type": "application/json" },
});

// Attach the access token to every request.
api.interceptors.request.use((config) => {
  const token = useAuthStore.getState().accessToken;
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// ── 401 handling with single-flight refresh ──
// On a 401, try to rotate the refresh token once and replay the request. If the
// refresh itself fails (or there is no refresh token — e.g. demo mode), log out.
let refreshing: Promise<string | null> | null = null;

async function rotate(): Promise<string | null> {
  const { refreshToken, setTokens, logout } = useAuthStore.getState();
  if (!refreshToken) {
    logout();
    return null;
  }
  try {
    // Bare axios (not `api`) to avoid recursive interceptors.
    const res = await axios.post(`${env.VITE_API_BASE_URL}/auth/refresh`, {
      refresh_token: refreshToken,
    });
    const d = res.data?.data;
    if (d?.access_token && d?.refresh_token) {
      setTokens(d.access_token, d.refresh_token);
      return d.access_token as string;
    }
    logout();
    return null;
  } catch {
    logout();
    return null;
  }
}

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as (InternalAxiosRequestConfig & { _retried?: boolean }) | undefined;
    const status = error.response?.status;

    // Never try to refresh the auth endpoints themselves.
    const url = original?.url ?? "";
    const isAuthCall = url.includes("/auth/");

    if (status === 401 && original && !original._retried && !isAuthCall) {
      original._retried = true;
      refreshing = refreshing ?? rotate();
      const newToken = await refreshing;
      refreshing = null;
      if (newToken) {
        original.headers.Authorization = `Bearer ${newToken}`;
        return api(original);
      }
    }

    if (status === 401 && isAuthCall) {
      // A failed login/refresh — do not loop.
      return Promise.reject(error);
    }

    return Promise.reject(error);
  },
);
